<script lang="ts" setup>
import { onMounted, ref, computed } from "vue";
import {
  CustomerService,
  CustomerListItem,
  CustomerOrderLimit,
} from "@workspace/open-api/cms";
import { initiateOrganizationLogin, popPendingCustomer } from "@two-ui/auth0";
import store from "@two-ui/store";
import { SelectOption } from "@wegift/two-components";
import TwoSelectField from "@two-components/components/TwoSelectField.vue";
import { useApiRetry } from "@workspace/packages/payout/src/composables/useApiRetry";

const { retry } = useApiRetry();

const emit = defineEmits(["change"]);
const fetchedCustomers = ref<CustomerListItem[]>([]);
const customerSelectOptions = ref<SelectOption[]>([]);
const hasOrgId = computed(() => store.state.hasOrgId);
const storedCustomer = computed(() =>
  fetchedCustomers.value.find(
    (customer) => customer.id == store.state.customerId
  )
);

const getCustomers = async () => {
  const customers = await retry(() => CustomerService.getCustomerList());
  customers.sort((a, b) => a.name.localeCompare(b.name));
  fetchedCustomers.value = customers;
};

const setCustomerSelectOptions = () => {
  customerSelectOptions.value = fetchedCustomers.value.map((customer) => ({
    id: customer.id,
    value: customer.id,
    label: customer.name,
  }));
};

/**
 * Switches to a different customer, handling organization authentication and store updates
 * @param customerId id of fetched customer from api.
 */
const switchCustomer = async (customerId: string) => {
  const customer = fetchedCustomers.value.find(
    (customer) => customer.id === customerId
  );

  if (customer) {
    const prevCustomerId = store.state.customerId;
    updateCustomerInStore(customer);

    // Call org auth when switching to a different customer, or when the current
    // token is missing org_id (e.g. returning user with a cached non-org-scoped token)
    if (customerId !== prevCustomerId || !hasOrgId.value) {
      if (!hasOrgId.value) {
        console.warn(
          "Current token is missing org_id. Initiating organization login to ensure org scoping."
        );
      }
      try {
        console.log(
          `Switching from customer ${prevCustomerId} to ${customer.id}`
        );
        await initiateOrganizationLogin(customer.id);
      } catch (error) {
        console.error("Failed to fetch token with organization:", error);
        alert(
          "Authentication failed. Please try selecting the customer again or contact support if the problem persists."
        );
      }
    }
  }
  emit("change");
};

const updateCustomerInStore = (customer: CustomerListItem) => {
  store.setCustomer(
    customer.id,
    customer.name,
    customer.contactEmailAddress,
    customer.features!,
    customer.source,
    customer.limitedAccess,
    customer.shopCountry
  );
  if (customer.defaultCurrency) {
    store.setCustomerDefaultCurrency(customer.defaultCurrency);
  }
  if (customer.discountConfig) {
    store.setCustomerDiscountConfig(customer.discountConfig);
  } else {
    store.setCustomerDiscountConfig(null);
  }
  store.setOrderLimits(customer.orderLimits || null);
};

const autoSelectFirstCustomer = async () => {
  if (fetchedCustomers.value.length >= 1) {
    await switchCustomer(fetchedCustomers.value[0].id);
  }
};

/**
 * Restores customer selection after Auth0 redirect if a valid pending selection exists.
 * Returns true if a customer was successfully restored, false otherwise.
 */
const restorePendingCustomerSelection = (): boolean => {
  const pendingCustomer = popPendingCustomer();
  if (!pendingCustomer) {
    return false;
  }

  const customer = fetchedCustomers.value.find(
    (c) => c.id === pendingCustomer.customerId
  );
  if (!customer) {
    return false;
  }

  updateCustomerInStore(customer);
  emit("change");
  return true;
};

onMounted(async () => {
  await getCustomers();
  setCustomerSelectOptions();

  if (restorePendingCustomerSelection()) {
    return; // Exit early, customer restored
  }

  if (storedCustomer.value) {
    // refresh stored customer values with fresh values fetched from api
    await switchCustomer(storedCustomer.value.id);
  } else {
    await autoSelectFirstCustomer();
  }
});
</script>

<template>
  <div>
    <TwoSelectField
      name="customer-select"
      v-if="fetchedCustomers.length > 1"
      :modelValue="storedCustomer?.id || ''"
      placeholder="Select a customer"
      :options="customerSelectOptions"
      class="w-48"
      @change="switchCustomer"
      theme="dark"
    />
    <p v-else-if="fetchedCustomers.length === 1">
      {{ storedCustomer?.name || "" }}
    </p>
    <small v-if="fetchedCustomers.length > 0 && !storedCustomer"
      >Please select customer</small
    >
  </div>
</template>
